import {
    QuantityDiscount,
    QuantityDiscountReduction,
    QuantityDiscountTicketTypeRef,
} from '@/interfaces/QuantityDiscount'

/**
 * Helpers de resolution/calcul des reductions quantitatives cote
 * guichet. Miroir fidele des helpers cote API/widget (plan 039).
 *
 * Le guichet travaille avec des prix en CENTIMES (comme l'API
 * cf. TicketType.price). Les fonctions ci-dessous prennent tout en
 * centimes et renvoient des montants en centimes.
 *
 * Aperçu client uniquement. Le total definitif est celui renvoye par
 * l'API sur le draft-order (cf. syncServerTotalSilently).
 */

function refToId(ref: QuantityDiscountTicketTypeRef): string | null {
    if (!ref) return null
    if (typeof ref === 'string') return ref
    return ref._id ?? null
}

/**
 * Retourne la regle applicable la plus specifique pour un
 * ticketTypeId donne, ou null si aucune ne matche.
 *
 *  1. Regle restreinte contenant `ticketTypeId` dans ticketTypesIds
 *  2. Regle globale (ticketTypesIds vide)
 */
export function findApplicableRule(
    ticketTypeId: string,
    rules: QuantityDiscount[],
): QuantityDiscount | null {
    if (!ticketTypeId || !rules || rules.length === 0) return null
    for (const rule of rules) {
        const refs = rule.ticketTypesIds || []
        if (refs.length === 0) continue
        if (refs.some((r) => refToId(r) === ticketTypeId)) {
            return rule
        }
    }
    for (const rule of rules) {
        const refs = rule.ticketTypesIds || []
        if (refs.length === 0) return rule
    }
    return null
}

/**
 * Resout le palier applicable pour un `count` donne sur une regle
 * (plan 039 phase 2 - multi-seuils). Prend le palier avec la plus
 * grande minQuantity que le count atteint.
 */
export function resolveApplicableTier(
    rule: QuantityDiscount,
    count: number,
): { minQuantity: number; reduction: QuantityDiscountReduction } | null {
    if (!rule) return null
    const additionalTiers = rule.additionalTiers || []
    const sortedTiers = additionalTiers
        .slice()
        .sort((a, b) => b.minQuantity - a.minQuantity)
    for (const tier of sortedTiers) {
        if (count >= tier.minQuantity) {
            return {
                minQuantity: tier.minQuantity,
                reduction: tier.reduction,
            }
        }
    }
    if (count >= rule.minQuantity) {
        return {
            minQuantity: rule.minQuantity,
            reduction: rule.reduction,
        }
    }
    return null
}

/**
 * Calcule le montant de remise unitaire POSITIF en centimes
 * (a retrancher au prix de base d'un ticket).
 *
 * `basePrice` en centimes.
 *  - percentage : basePrice * amount / 10000 (amount en basis points)
 *  - flat       : min(amount, basePrice), cap au prix nominal
 *  - gridPrice  : basePrice - entry.amount. Si prix cible >= basePrice,
 *                 remise = 0 (jamais de majoration).
 *
 * Retourne 0 si basePrice <= 0 (invitations, gratuites).
 *
 * `overrideReduction` : utilise pour les paliers multi-seuils, permet
 * d'appliquer la reduction du palier atteint sans muter la regle.
 */
export function computeUnitDiscount(
    rule: QuantityDiscount,
    ticketTypeId: string,
    basePrice: number,
    overrideReduction?: QuantityDiscountReduction,
): number {
    if (!rule || basePrice <= 0) return 0
    const reduction = overrideReduction || rule.reduction
    switch (reduction?.type) {
        case 'percentage': {
            const discount = Math.round(
                (basePrice * reduction.amount) / 10000,
            )
            return Math.max(0, discount)
        }
        case 'flat': {
            return Math.min(reduction.amount, basePrice)
        }
        case 'gridPrice': {
            const entry = (rule.gridPrice || []).find(
                (gp) => refToId(gp.ticketTypeId) === ticketTypeId,
            )
            if (!entry) return 0
            if (entry.amount >= basePrice) return 0
            return basePrice - entry.amount
        }
        default:
            return 0
    }
}

export interface QuantityDiscountLine {
    ruleId: string
    label: string
    /** Montant total en CENTIMES a retrancher au sous-total. */
    amount: number
}

/**
 * Aggrege les reductions quantitatives par regle a partir d'un
 * panier de tickets. Retourne une ligne par regle applicable, prete
 * a etre affichee dans le recap panier guichet.
 *
 * `tickets` : liste des billets (chaque billet = un TicketType avec
 * `_id` et `price` en centimes). Les tickets sans regle applicable
 * ou avec seuil non atteint sont ignores.
 */
export function computeQuantityDiscountLines(
    rules: QuantityDiscount[],
    tickets: Array<{ _id: string; price: number }>,
): QuantityDiscountLine[] {
    if (!rules || rules.length === 0 || !tickets || tickets.length === 0) {
        return []
    }
    const bucketsByRule = new Map<
        string,
        {
            rule: QuantityDiscount
            items: Array<{ ticketTypeId: string; basePrice: number }>
        }
    >()
    for (const t of tickets) {
        if (!t || !t._id) continue
        const rule = findApplicableRule(t._id, rules)
        if (!rule) continue
        const bucket = bucketsByRule.get(rule._id) || { rule, items: [] }
        bucket.items.push({ ticketTypeId: t._id, basePrice: t.price || 0 })
        bucketsByRule.set(rule._id, bucket)
    }

    const lines: QuantityDiscountLine[] = []
    bucketsByRule.forEach((bucket) => {
        const { rule, items } = bucket
        const tier = resolveApplicableTier(rule, items.length)
        if (!tier) return
        let amount = 0
        for (const it of items) {
            const unit = computeUnitDiscount(
                rule,
                it.ticketTypeId,
                it.basePrice,
                tier.reduction,
            )
            if (unit > 0) amount += unit
        }
        if (amount <= 0) return
        lines.push({ ruleId: rule._id, label: rule.label, amount })
    })
    return lines
}

/**
 * Somme des reductions quantitatives applicables en CENTIMES.
 * Utile pour retrancher directement du sous-total.
 */
export function sumQuantityDiscountLines(
    lines: QuantityDiscountLine[],
): number {
    return (lines || []).reduce((acc, l) => acc + (l.amount || 0), 0)
}
